import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  useRegisteredShortcuts,
  KeyboardShortcut,
  useHelpDialogShortcuts,
  KeyboardShortcutCategory,
} from 'hooks/useKeyboardShortcuts';
import { useTranslation } from 'react-i18next';
import { ThemeProvider } from '@emotion/react';
import { watchdutyDarkTheme } from 'theme';

export const KeyboardShortcutsDialog = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  useHelpDialogShortcuts(() => setIsOpen((prev) => !prev));

  const categoryOrder: Array<KeyboardShortcutCategory> = [
    'common',
    'baseMap',
    'mapLayers',
    'mapTools',
    'camera',
  ];

  const shortcutsByCategory: Record<
    KeyboardShortcutCategory,
    KeyboardShortcut[]
  > = useRegisteredShortcuts().reduce(
    (acc, shortcut) => {
      const { category } = shortcut;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(shortcut);
      return acc;
    },
    {} as Record<KeyboardShortcutCategory, KeyboardShortcut[]>,
  );

  // Sort shortcuts by keys within each category
  categoryOrder.forEach((category) => {
    shortcutsByCategory[category]?.sort((a, b) => a.keys.localeCompare(b.keys));
  });
  const shortcutsByCategoryInOrder = categoryOrder
    .map((category) => [category, shortcutsByCategory[category]] as const)
    .filter(([, shortcuts]) => shortcuts?.length);

  return (
    <ThemeProvider theme={watchdutyDarkTheme}>
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        slotProps={{
          paper: {
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
          },
        }}
      >
        <DialogTitle>{t('keyboardShortcuts.dialog.title')}</DialogTitle>
        <DialogContent>
          {shortcutsByCategoryInOrder.map(([category, shortcuts]) => (
            <Grid2 key={category} container padding={0.5}>
              <Grid2 size={12} marginBottom={0}>
                <Typography component="h4" sx={{ fontWeight: 'bold' }}>
                  {t(`keyboardShortcuts.categories.${category}`)}
                </Typography>
              </Grid2>
              <Grid2 size={12} padding={0}>
                <List>
                  {shortcuts.map(({ description, keys }) => (
                    <ListItem
                      key={`${description}-${keys}`}
                      sx={{
                        marginBottom: -1,
                      }}
                    >
                      <Box
                        sx={{
                          width: '150px',
                          display: 'flex',
                        }}
                      >
                        <Chip variant="keyboard-shortcut" label={keys} />
                      </Box>
                      <ListItemText primary={description} />
                    </ListItem>
                  ))}
                </List>
              </Grid2>
            </Grid2>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsOpen(false)}
            variant="text"
            sx={{ mb: 1, color: 'white' }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
